import clsx from 'clsx';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { ReactComponent as Calendar } from '@quick/assets/icons/form/calendar.svg';
import { ReactComponent as Trash } from '@quick/assets/icons/trash.svg';
import { Button } from '@quick/components/Button';
import { TimePicker } from '@quick/components/FlatPicker';
import { Switch } from '@quick/components/Switch';
import type { ICity } from '@quick/go-api-types/services/cities';
import {
  timezoneDateToFieldValue,
  userInputToTimezoneDate,
} from '@quick/utils';

import { useAppTranslation } from '@/app/i18n/client';
import { ReactComponent as Plus2 } from '@/assets/plus2.svg';
import { daysAndMonths } from '@/layers/core/deal/mappers/calendarMapper';
import { useFetch } from '@/layers/core/deal/services/useFetch';

import {
  animationClassName,
  gapClassName,
  groupClassName,
  onlyBorderClassName,
} from './classNames';
import { useFormDataContext } from './FormDataContext';
import { CommonTitle } from './Titles';
import type { IForm } from './types';

export const Dates = () => {
  const { t } = useAppTranslation(['common', 'demand']);
  const {
    watch,
    control,
    formState: { errors },
    getValues,
  } = useFormContext<IForm>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'dates',
  });

  const { data } = useFormDataContext();

  const modalityId = watch('modality_id');

  const calendarTranslation = daysAndMonths(t);

  const cityId = watch('stops').find((add) => add.city_id)?.city_id;
  const { data: cities } = useFetch<ICity[]>(
    !!cityId && {
      url: `/services/cities`,
      params: { city_id: cityId },
    }
  );

  const timezone = cities?.find((city) => `${city.id}` === `${cityId}`)
    ?.timezone?.name;

  const minDayJs = dayjs()
    .tz(timezone ?? undefined)
    .add(1.25, 'hour');
  const minDate = minDayJs.format('YYYY-MM-DD HH:mm');

  const show = !!fields.length;
  if (!modalityId || !data?.lines?.length) return null;
  return (
    <div
      className={clsx(
        'flex flex-col',
        groupClassName,
        gapClassName,
        animationClassName,
        onlyBorderClassName
      )}
    >
      <div className="flex flex-wrap gap-4">
        <CommonTitle title={t('common:dates')} Icon={Calendar} />
        <div className="ml-auto flex flex-wrap justify-end gap-4">
          <Switch
            id="programService"
            checked={!!fields.length}
            onChange={({ target: { checked } }) => {
              if (checked) append({ date_time: '', uuid: nanoid() });
              else remove();
            }}
            className="group items-center justify-end gap-6"
          >
            <span className="group-focus-within:underline">
              {t('demand:schedule')}
            </span>
          </Switch>
          <Button
            className="flex items-center gap-4"
            kind="secondary"
            variant="accept"
            // disabled={!show}
            onClick={() => {
              if (fields.length <= 1000)
                append({ date_time: '', uuid: nanoid() });
            }}
            type="button"
          >
            <Plus2 className="w-4" />
            {t('common:add')}
          </Button>
        </div>
      </div>
      {show && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('demand:dates.timezoneHelp')}
        </p>
      )}
      <div className="grid w-full gap-4 empty:hidden sm:grid-cols-[repeat(auto-fit,minmax(14rem,1fr))]">
        {show &&
          fields?.map((date, i) => (
            <div className="flex w-full min-w-0 flex-col" key={date.uuid}>
              <div className="flex w-full min-w-0 items-center">
                <Controller
                  name={`dates.${i}.date_time`}
                  rules={{
                    required: t('common:msg.required_field'),
                    validate: (v) => {
                      if (dayjs(v).isBefore(minDayJs, 'minute'))
                        return t('common:msg.min_equal_number', {
                          min: minDayJs.format('YYYY-MM-DD HH:mm'),
                        });
                    },
                  }}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      isOnModal
                      className="w-full"
                      innerClassName="[&>input]:pr-8 [&>input]:min-w-0"
                      ref={(dt) => {
                        field.ref(dt?.flatpickr.input);
                      }}
                      id={`${i}date`}
                      onClose={(_, currentDate) => {
                        const dateWithTimezone = userInputToTimezoneDate(
                          currentDate,
                          timezone
                        );
                        field.onChange(dateWithTimezone);
                      }}
                      onChange={(_, currentDate) => {
                        const dateWithTimezone = userInputToTimezoneDate(
                          currentDate,
                          timezone
                        );
                        field.onChange(dateWithTimezone);
                      }}
                      options={{
                        dateFormat: 'Y-m-d H:i',
                        enableTime: true,
                        minDate,
                        allowInput: true,
                        disableMobile: true,
                        locale: calendarTranslation,
                        allowInvalidPreload: true,
                        parseDate: (date) => {
                          const cleanDate = String(date).replaceAll(
                            // replace all characters that are not part of a date string
                            /[^\dZT\-:./\s]+/g,
                            ''
                          );
                          const parsedDate = dayjs(cleanDate);
                          if (parsedDate.isValid()) return parsedDate.toDate();
                          return new Date();
                        },
                      }}
                      placeholder={t(
                        'demand:dates.form.serviceDatePlaceholder'
                      )}
                      value={
                        field.value
                          ? timezoneDateToFieldValue(field.value, timezone)
                          : ''
                      }
                    />
                  )}
                />
                <button
                  className="hover:bg-gray-light -ml-8 mr-2 aspect-square rounded-full p-1"
                  onClick={() => remove(i)}
                  type="button"
                >
                  <Trash className="text-red-600" />
                </button>
              </div>
              <p className="text-right text-xs font-semibold text-red-500 empty:hidden dark:text-red-300">
                {errors.dates?.[i]?.date_time?.message || ''}
              </p>
            </div>
          ))}
      </div>
      {!!watch(`dates.${fields.length - 1}.date_time`) && (
        <Button
          className="flex items-center gap-4 sm:self-center"
          kind="secondary"
          variant="accept"
          onClick={() => {
            if (fields.length > 1000) return;
            const lastDate = getValues('dates').at(-1)?.date_time;
            if (!lastDate) return;
            append({
              date_time: dayjs(lastDate).add(1, 'day').toISOString(),
              uuid: nanoid(),
            });
          }}
          type="button"
        >
          <Plus2 className="w-4" />
          {t('demand:dates.cloneDate')}
        </Button>
      )}
    </div>
  );
};
